// =============================================================================
// MeuDriver — Q4: Hook de avaliações do estudante pelo instrutor
// Tabela: instructor_student_reviews
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'

// ─── Tipos ───────────────────────────────────────────────────────────────────

export interface InstructorStudentReviewRow {
  id: string
  booking_id: string
  instructor_id: string
  student_id: string
  rating: number
  comment: string | null
  created_at: string
}

/** Avaliação com dados do instrutor (para o aluno ver) */
export interface StudentReceivedReview extends InstructorStudentReviewRow {
  instructor_name: string | null
  instructor_avatar: string | null
}

/** Avaliação com dados do aluno (para o instrutor ver) */
export interface InstructorGivenReview extends InstructorStudentReviewRow {
  student_name: string | null
  booking_scheduled_at: string | null
}

// ─── Chaves de query ─────────────────────────────────────────────────────────

const KEYS = {
  myReviews:        ['student_reviews', 'mine']          as const,
  instructorGiven:  ['student_reviews', 'instructor']    as const,
  byBooking:        (id: string) => ['student_reviews', 'booking', id] as const,
}

// ─── useMyStudentReviews ──────────────────────────────────────────────────────

/**
 * Avaliações que o estudante autenticado recebeu dos instrutores.
 * Inclui dados do instrutor via JOIN no PostgREST.
 */
export function useMyStudentReviews() {
  return useQuery({
    queryKey: KEYS.myReviews,
    staleTime: 2 * 60_000,
    queryFn: async (): Promise<StudentReceivedReview[]> => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return []

      const { data, error } = await supabase
        .from('instructor_student_reviews')
        .select(`
          *,
          instructors!instructor_id (
            profiles!profile_id ( full_name, avatar_url )
          )
        `)
        .eq('student_id', user.id)
        .order('created_at', { ascending: false })

      if (error) throw new Error(error.message)

      return ((data ?? []) as Record<string, unknown>[]).map((row) => {
        const inst = row.instructors as Record<string, unknown> | null
        const profile = inst?.profiles as Record<string, unknown> | null
        return {
          id:               row.id as string,
          booking_id:       row.booking_id as string,
          instructor_id:    row.instructor_id as string,
          student_id:       row.student_id as string,
          rating:           row.rating as number,
          comment:          row.comment as string | null,
          created_at:       row.created_at as string,
          instructor_name:  (profile?.full_name as string | null) ?? null,
          instructor_avatar:(profile?.avatar_url as string | null) ?? null,
        } satisfies StudentReceivedReview
      })
    },
  })
}

// ─── useInstructorStudentReviews ──────────────────────────────────────────────

/**
 * Avaliações que o instrutor autenticado deu aos alunos.
 */
export function useInstructorStudentReviews() {
  return useQuery({
    queryKey: KEYS.instructorGiven,
    staleTime: 2 * 60_000,
    queryFn: async (): Promise<InstructorGivenReview[]> => {
      const { data, error } = await supabase
        .from('instructor_student_reviews')
        .select(`
          *,
          profiles!student_id ( full_name ),
          bookings!booking_id ( scheduled_at )
        `)
        .order('created_at', { ascending: false })

      if (error) throw new Error(error.message)

      return ((data ?? []) as Record<string, unknown>[]).map((row) => {
        const profile = row.profiles as Record<string, unknown> | null
        const booking = row.bookings as Record<string, unknown> | null
        return {
          id:                    row.id as string,
          booking_id:            row.booking_id as string,
          instructor_id:         row.instructor_id as string,
          student_id:            row.student_id as string,
          rating:                row.rating as number,
          comment:               row.comment as string | null,
          created_at:            row.created_at as string,
          student_name:          (profile?.full_name as string | null) ?? null,
          booking_scheduled_at:  (booking?.scheduled_at as string | null) ?? null,
        } satisfies InstructorGivenReview
      })
    },
  })
}

// ─── useBookingStudentReview ──────────────────────────────────────────────────

/**
 * Verifica se já existe review para um booking específico.
 */
export function useBookingStudentReview(bookingId: string | undefined) {
  return useQuery({
    queryKey: KEYS.byBooking(bookingId ?? ''),
    enabled: Boolean(bookingId),
    staleTime: 30_000,
    queryFn: async (): Promise<InstructorStudentReviewRow | null> => {
      const { data, error } = await supabase
        .from('instructor_student_reviews')
        .select('*')
        .eq('booking_id', bookingId!)
        .maybeSingle()

      if (error) throw new Error(error.message)
      return data as InstructorStudentReviewRow | null
    },
  })
}

// ─── useSubmitStudentReview ───────────────────────────────────────────────────

/**
 * Submete avaliação do aluno via RPC submit_student_review.
 * Apenas o instrutor do booking pode chamar.
 */
export function useSubmitStudentReview() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({
      bookingId,
      rating,
      comment,
    }: {
      bookingId: string
      rating: number
      comment?: string
    }) => {
      const { data, error } = await supabase.rpc('submit_student_review', {
        p_booking_id: bookingId,
        p_rating:     rating,
        p_comment:    comment ?? null,
      })
      if (error) throw new Error(error.message)
      return data as string  // UUID do novo registro
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: KEYS.instructorGiven })
      qc.invalidateQueries({ queryKey: KEYS.myReviews })
      qc.invalidateQueries({ queryKey: KEYS.byBooking(variables.bookingId) })
      toast.success('Avaliação enviada com sucesso!')
    },
    onError: (err: Error) => {
      toast.error(`Erro ao enviar avaliação: ${err.message}`)
    },
  })
}
